import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ChevronDown } from 'lucide-react';
import { db, handleFirestoreError, OperationType } from '../lib/firebase';
import { collection, addDoc, setDoc, doc, serverTimestamp } from 'firebase/firestore';
import { cn } from '../lib/utils';
import { gemAt } from '../constants/sampleImages';

const GEM_TYPES = [
  'Sapphire', 'Ruby', 'Emerald', 'Spinel', 'Diamond', 
  'Alexandrite', 'Tourmaline', 'Aquamarine', 'Tanzanite', 'Other'
];

const CUT_OPTIONS = ['Oval', 'Cushion', 'Round', 'Pear', 'Emerald Cut', 'Marquise', 'Cabochon', 'Other'];
const CLARITY_OPTIONS = ['Eye Clean', 'Loupe Clean', 'Slightly Included', 'Moderately Included', 'Heavily Included'];
const TREATMENT_OPTIONS = ['Unheated', 'Heated', 'Unknown'];
const CERTIFICATION_OPTIONS = ['None', 'GIA', 'GRS', 'GIC', 'Local Lab'];
const ORIGIN_OPTIONS = ['Sri Lanka', 'Madagascar', 'Myanmar', 'Thailand', 'Mozambique', 'Tanzania', 'Other'];
const SHAPE_OPTIONS = ['Oval', 'Cushion', 'Round', 'Pear', 'Emerald', 'Marquise', 'Trilliant', 'Princess', 'Heart', 'Other'];

export const AddListingPage = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedGemType, setSelectedGemType] = useState('Sapphire');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userProfile) {
      navigate('/login');
      return;
    }
    setLoading(true);
    
    const formData = new FormData(e.currentTarget);
    const listingData = {
      vendorId: userProfile.uid,
      title: formData.get('title'),
      gemType: selectedGemType,
      caratWeight: Number(formData.get('weight')),
      price: Number(formData.get('price')),
      currency: 'USD',
      cut: formData.get('cut'),
      clarity: formData.get('clarity'),
      treatment: formData.get('treatment'),
      certification: formData.get('certification'),
      origin: formData.get('origin'),
      shape: formData.get('shape'),
      dimensions: formData.get('dimensions'),
      description: formData.get('description'),
      images: [gemAt(Math.floor(Math.random() * 10))],
      status: 'ACTIVE',
      createdAt: serverTimestamp(),
    };

    try {
      const newListingRef = doc(collection(db, 'listings'));
      await setDoc(newListingRef, listingData);
      navigate('/marketplace');
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, 'listings');
      alert("Failed to add listing.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-6 px-4 pb-24">
      <button 
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-slate-400 font-bold text-[10px] uppercase tracking-widest mb-6 hover:text-primary transition-colors"
      >
        <ArrowLeft size={14} />
        Back
      </button>

      <div className="bg-white rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/50 overflow-hidden">
        <div className="px-8 pt-8 pb-4">
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">List Gem</h1>
          <p className="text-slate-400 text-xs font-medium">Marketplace Submission</p>
          {!userProfile && (
            <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-4 py-2 font-medium">
              Sign in when you are ready to publish your listing.
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-7">
          {/* LISTING TITLE */}
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Listing Title</label>
            <input 
              name="title" 
              type="text"
              placeholder="e.g. Natural Unheated Ceylon Blue Sapphire" 
              className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none" 
              required 
            />
          </div>

          {/* GEM TYPE */}
          <div className="space-y-3">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Gem Type</label>
            <div className="flex flex-wrap gap-2">
              {GEM_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setSelectedGemType(type)}
                  className={cn(
                    "px-4 py-2 rounded-full text-xs font-bold transition-all border",
                    selectedGemType === type 
                      ? "bg-primary border-primary text-white shadow-lg shadow-primary/30 scale-105" 
                      : "bg-white border-slate-100 text-slate-500 hover:border-slate-300"
                  )}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* WEIGHT AND PRICE */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Carat Weight</label>
              <input 
                name="weight" 
                type="number" 
                step="0.01" 
                placeholder="e.g. 2.5" 
                className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none" 
                required 
              />
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Price (USD)</label>
              <input 
                name="price" 
                type="number" 
                placeholder="e.g. 1500" 
                className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none" 
                required 
              />
            </div>
          </div>

          {/* CUT AND CLARITY */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Cut</label>
              <div className="relative">
                <select 
                  name="cut" 
                  className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>Select cut</option>
                  {CUT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Clarity</label>
              <div className="relative">
                <select 
                  name="clarity" 
                  className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>Select clarity</option>
                  {CLARITY_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>
          </div>

          {/* TREATMENT AND CERTIFICATION */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Treatment</label>
              <div className="relative">
                <select 
                  name="treatment" 
                  className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>Select</option>
                  {TREATMENT_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Certification</label>
              <div className="relative">
                <select 
                  name="certification" 
                  className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                  defaultValue=""
                  required
                >
                  <option value="" disabled>Select</option>
                  {CERTIFICATION_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
                <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
              </div>
            </div>
          </div>

          {/* ORIGIN AND SHAPE */}
          <div className="grid grid-cols-2 gap-4">
             <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Origin</label>
                <div className="relative">
                  <select 
                    name="origin" 
                    className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                    defaultValue=""
                  >
                    <option value="" disabled>Select origin</option>
                    {ORIGIN_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Shape</label>
                <div className="relative">
                  <select 
                    name="shape" 
                    className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 appearance-none focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none"
                    defaultValue=""
                  >
                    <option value="" disabled>Select shape</option>
                    {SHAPE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={18} />
                </div>
              </div>
          </div>

          {/* DIMENSIONS */}
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Dimensions (L x W x D mm)</label>
            <input 
              name="dimensions" 
              type="text"
              placeholder="e.g. 10.5 x 8.2 x 5.1" 
              className="w-full h-14 px-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none" 
            />
          </div>

          {/* DESCRIPTION */}
          <div className="space-y-2">
            <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest block">Description</label>
            <textarea 
              name="description" 
              rows={4}
              placeholder="Additional details, condition notes..." 
              className="w-full p-5 rounded-2xl bg-slate-50 border border-slate-100 font-medium text-slate-900 placeholder:text-slate-300 focus:ring-2 focus:ring-primary/20 focus:bg-white transition-all outline-none resize-none" 
            />
          </div>

          {/* ACTION BUTTON */}
          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-5 bg-primary text-white rounded-2xl font-black shadow-xl shadow-primary/30 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all flex items-center justify-center gap-3 text-lg disabled:opacity-50 mt-4"
          >
            {loading ? 'Publishing...' : 'List Your Gem'}
            {!loading && <ArrowRight size={22} />}
          </button>
        </form>
      </div>
    </div>
  );
};
